import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { formatRM } from '@/features/billing/money';
import {
  useApproveLocumPay,
  useManagerLocumPay,
  type LocumPayWeek,
} from '@/features/locum-pay/api';
import { locumPayErrorMessage } from '@/features/locum-pay/errors';
import { payHistory, weekLabel } from '@/features/locum-pay/format';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import styles from './LocumPayPages.module.css';

const hours = (minutes: number) => `${(minutes / 60).toFixed(2)} h`;
const period = (row: LocumPayWeek) => weekLabel(row);

function statusTone(status: LocumPayWeek['status']) {
  if (status === 'approved' || status === 'paid') return 'success' as const;
  if (status === 'running') return 'warning' as const;
  return 'warning' as const;
}

export function ManagerLocumPayPage() {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const canApprove = hasPermission('locum_pay:approve');
  const queue = useManagerLocumPay();
  const approval = useApproveLocumPay();
  const toast = useToast();

  const approve = (row: LocumPayWeek) =>
    approval.mutate(
      { id: row.id },
      {
        onSuccess: () => {
          toast.show({
            tone: 'success',
            title: t('locumPay.approved'),
          });
        },
      },
    );

  const apiError = isApiError(approval.error) ? approval.error : undefined;
  return (
    <PageContainer>
      <div className={styles.stack}>
        <Card title={t('locumPay.managerTitle')} flush>
          <DataTable
            caption={t('locumPay.managerTitle')}
            rows={queue.data}
            rowKey={(row) => row.id}
            loading={queue.isLoading}
            error={queue.error}
            onRetry={() => void queue.refetch()}
            empty={
              <EmptyState
                title={t('locumPay.noReview')}
                body={t('locumPay.noReviewBody')}
              />
            }
            columns={[
              {
                key: 'locum',
                header: t('locumPay.locum'),
                render: (row) => (
                  <div>
                    <strong>{row.locumName}</strong>
                    <span className={styles.muted}>{row.branchName}</span>
                  </div>
                ),
              },
              { key: 'week', header: t('locumPay.week'), render: period },
              {
                key: 'attendance',
                header: t('locumPay.attendance'),
                render: (row) => hours(row.attendanceMinutes),
              },
              {
                key: 'rate',
                header: t('locumPay.rate'),
                render: (row) => `${formatRM(row.hourlyRateSen)}/h`,
              },
              {
                key: 'gross',
                header: t('locumPay.grossPay'),
                render: (row) => <strong>{formatRM(row.grossPaySen)}</strong>,
              },
              {
                key: 'status',
                header: t('locumPay.statusLabel'),
                render: (row) => (
                  <div className={styles.statusCell}>
                    <Badge tone={statusTone(row.status)}>
                      {t(`locumPay.status.${row.status}`)}
                    </Badge>
                    {row.correctionRequired && (
                      <div className={styles.correctionBlock}>
                        <span className={styles.correction}>
                          <AlertTriangle size={14} />
                          {t('locumPay.correctionCount', {
                            count: row.correctionRecordIds.length,
                          })}
                        </span>
                        {row.correctionRecordIds.map((recordId, index) => (
                          <Link
                            key={recordId}
                            className={styles.correctionLink}
                            to={`/setup/attendance?recordId=${encodeURIComponent(recordId)}`}
                          >
                            {t('locumPay.correctAttendance', {
                              count: index + 1,
                            })}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'history',
                header: t('locumPay.history'),
                render: (row) => {
                  const lines = payHistory(row);
                  return lines.length ? (
                    <div className={styles.statusCell}>
                      {lines.map((line) => (
                        <span key={line} className={styles.muted}>
                          {line}
                        </span>
                      ))}
                    </div>
                  ) : (
                    '—'
                  );
                },
              },
              {
                key: 'actions',
                header: t('locumPay.actions'),
                render: (row) =>
                  canApprove && row.status === 'submitted' ? (
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={row.correctionRequired}
                      loading={
                        approval.isPending && approval.variables?.id === row.id
                      }
                      onClick={() => approve(row)}
                    >
                      {t('locumPay.approve')}
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>
        {apiError && (
          <ErrorState
            error={apiError}
            body={locumPayErrorMessage(apiError, t)}
            onRetry={() => approval.reset()}
          />
        )}
      </div>
    </PageContainer>
  );
}
